"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import { AnimatePresence, motion } from "framer-motion";

const COOKIE_NAME = "ppf_cookie_consent";
const SHOW_DELAY = 500; // ms — et vältida vilkumist esimesel renderdusel

function getCookie(name: string): string | undefined {
  if (typeof document === "undefined") return undefined;
  const match = document.cookie
    .split("; ")
    .find((row) => row.startsWith(`${name}=`));
  return match ? decodeURIComponent(match.split("=")[1]) : undefined;
}

export default function CookieConsent() {
  const [checked, setChecked] = useState(false);
  const [visible, setVisible] = useState(false);

  // Esialgne renderdus on null — vältimaks hydration mismatch'i.
  useEffect(() => {
    setChecked(true);
    const consent = getCookie(COOKIE_NAME);
    if (!consent) {
      // Väike viivitus, et baner ei ilmuks kohe esimesel kaadrid.
      const timer = setTimeout(() => setVisible(true), SHOW_DELAY);
      return () => clearTimeout(timer);
    }
    setVisible(false);
  }, []);

  const handleAccept = () => {
    document.cookie = "ppf_cookie_consent=accepted; path=/; max-age=3600; SameSite=Lax";
    setVisible(false);
  };

  // Kuni komponent pole klientil määranud oma olekut, ei renderda midagi.
  if (!checked) {
    return null;
  }

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          key="cookie-consent"
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 24 }}
          transition={{ duration: 0.25, ease: "easeOut" }}
          className="fixed bottom-0 left-0 right-0 z-[9998] px-4 pb-4 md:px-6 md:pb-6"
          role="dialog"
          aria-live="polite"
          aria-label="Küpsiste nõusolek"
        >
          <div className="mx-auto max-w-3xl border border-white/10 bg-secondary px-5 py-4 md:px-6 md:py-5 shadow-2xl">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <p className="font-body text-[14px] leading-relaxed text-text-muted">
                PPF Garage kasutab küpsiseid, et pakkuda ja parandada oma teenuseid ning analüüsida liiklust.{" "}
                <Link
                  href="/kupsised"
                  className="font-medium text-accent underline-offset-2 transition-colors hover:text-text-main hover:underline"
                >
                  Loe lähemalt küpsiste kohta
                </Link>
                .
              </p>
              <button
                type="button"
                onClick={handleAccept}
                className="shrink-0 bg-accent px-5 py-2.5 font-body text-[14px] font-medium text-primary transition-[transform,background-color] duration-200 ease-out hover:scale-[1.03] hover:bg-accent/90 active:scale-100"
              >
                Nõustun
              </button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
